import { appConfirm } from "@/lib/appDialog";
import { useEffect, useState } from "react";
import { api, formatApiError, OPP_STATUS_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Eye, Upload } from "lucide-react";

const EMPTY = {
  title: "", description: "", program_name: "", seats: 20,
  duration_weeks: 12, total_training_hours: "", city: "", category: "", cover_image_url: "",
  tags: "", requirements: "", outcomes: "", status: "draft", course_id: "",
  association_allocations: [], admin_notes: "",
};

export default function OpportunitiesAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [opps, setOpps] = useState([]);
  const [courses, setCourses] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/opportunities/manage");
      setOpps(data.opportunities);
      if (isAdmin) {
        try {
          const { data: cs } = await api.get("/courses");
          setCourses(cs.courses || []);
        } catch { /* ignore */ }
        try {
          const { data: directory } = await api.get(
            "/directory?role=association"
          );
          setAssociations(
            (directory.users || []).filter(
              (association) => association.is_active !== false
            )
          );
        } catch {
          setAssociations([]);
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => {
    setForm({ ...EMPTY, association_allocations: [] });
    setEditingId(null);
    setShowForm(true);
  };
  const openEdit = (o) => {
    setForm({
      ...EMPTY, ...o,
      tags: (o.tags || []).join(", "),
      requirements: (o.requirements || []).join("\n"),
      outcomes: (o.outcomes || []).join("\n"),
      association_allocations: (o.association_allocations || []).map(
        (allocation) => ({ ...allocation })
      ),
    });
    setEditingId(o.id); setShowForm(true);
  };

  const uploadCover = async (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("اختر صورة صالحة"); return; }
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); fd.append("context", "opportunity_image"); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setForm({ ...form, cover_image_url: "/api/files/" + data.file_id }); toast.success("تم رفع صورة الفرصة"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setUploading(false); }
  };

  const handleSave = async () => {
    const allocations = (form.association_allocations || []).map(
      (allocation) => ({
        id: allocation.id || undefined,
        association_id: allocation.association_id,
        delivery_mode: allocation.delivery_mode || "onsite",
        city:
          (allocation.delivery_mode || "onsite") === "remote"
            ? ""
            : (allocation.city || "").trim(),
        seats: Number(allocation.seats),
      })
    );

    if (isAdmin && !editingId && allocations.length === 0) {
      toast.error("أضف جمعية واحدة على الأقل كموقع تنفيذ للفرصة");
      return;
    }
    if (
      allocations.some(
        (allocation) =>
          !allocation.association_id
          || !["onsite", "remote", "hybrid"].includes(allocation.delivery_mode)
          || (allocation.delivery_mode !== "remote" && !allocation.city)
          || !Number.isInteger(allocation.seats)
          || allocation.seats < 1
      )
    ) {
      toast.error("أكمل الجمعية والنمط والمدينة والمقاعد لكل موقع تنفيذ");
      return;
    }

    const totalHours = Number(form.total_training_hours);
    if (!Number.isInteger(totalHours) || totalHours < 1 || totalHours > 5000) { toast.error("أدخل إجمالي ساعات صحيحًا بين 1 و5000"); return; }

    setSaving(true);
    try {
      // Auto-compute training_end from training_start + duration_weeks
      const weeks = parseInt(form.duration_weeks) || 0;
      let computedEnd = null;
      if (form.training_start) {
        const s = new Date(form.training_start);
        if (!isNaN(s)) {
          const e = new Date(s); e.setDate(e.getDate() + weeks * 7);
          computedEnd = e.toISOString().slice(0, 10);
        }
      }
      const payload = {
        ...form,
        association_allocations: allocations,
        seats: parseInt(form.seats) || 0,
        duration_weeks: weeks,
        total_training_hours: totalHours,
        training_end: computedEnd,
        application_start: null,
        application_end: null,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        requirements: form.requirements.split("\n").map((s) => s.trim()).filter(Boolean),
        outcomes: form.outcomes.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (!isAdmin) delete payload.course_id;
      if (editingId) {
        await api.patch(`/opportunities/${editingId}`, payload);
        toast.success("تم تحديث الفرصة");
      } else {
        await api.post("/opportunities", payload);
        toast.success("تم إنشاء الفرصة");
      }
      setShowForm(false); load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!await appConfirm("هل أنت متأكد من حذف هذه الفرصة؟")) return;
    try {
      await api.delete(`/opportunities/${id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout>
      <div data-testid="opportunities-admin">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">فرص التدريب التعاوني</div>
            <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">إدارة الفرص</h1>
          </div>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 transition-colors"
            data-testid="new-opp-btn"
          >
            <Plus className="h-5 w-5" />
            فرصة جديدة
          </button>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">العنوان</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">الجمعية المُقدِّمة</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">البرنامج</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">المقاعد</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">الحالة</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {opps.map((o) => (
                    <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`opp-row-${o.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{o.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{o.city} · {o.duration_weeks} أسبوع</div>
                        {(o.association_allocations || []).length > 0 && (
                          <div className="mt-1 text-xs font-medium text-teal-700">
                            {o.association_allocations.length} مواقع تنفيذ
                          </div>
                        )}
                        {o.admin_notes && (
                          <div className="mt-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-900" data-testid={`opp-admin-note-${o.id}`}>
                            <span className="font-bold">ملاحظة الجمعية للإدارة: </span>{o.admin_notes}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {o.submitter_name ? (
                          <div>
                            <div className="font-semibold text-slate-900 text-sm">{o.submitter_name}</div>
                            <div className="text-[11px] text-slate-500">
                              {o.submitter_role === "association" ? "جمعية" : "الإدارة"}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{o.program_name}</td>
                      <td className="px-4 py-3 text-slate-600 arabic-numerals">{o.seats_filled || 0}/{o.seats}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold
                          ${o.status === "published" ? "bg-emerald-50 text-emerald-700"
                            : o.status === "draft" ? "bg-slate-100 text-slate-700"
                            : "bg-amber-50 text-amber-700"}`}>
                          {OPP_STATUS_LABELS[o.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <a href={`/opportunities/${o.id}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-teal-700">
                            <Eye className="h-4 w-4" />
                          </a>
                          <button onClick={() => openEdit(o)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-teal-700" data-testid={`edit-opp-${o.id}`}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDelete(o.id)} className="p-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid={`delete-opp-${o.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {opps.length === 0 && (
                    <tr><td colSpan={6} className="p-12 text-center text-slate-500">لا توجد فرص بعد</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showForm && (
          <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-start pt-8 pb-8 overflow-y-auto p-4" onClick={() => setShowForm(false)}>
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl p-5 my-auto" onClick={(e) => e.stopPropagation()} data-testid="opp-form-modal">
              <h3 className="text-2xl font-heading font-extrabold text-slate-900 mb-6">
                {editingId ? "تعديل الفرصة" : "فرصة جديدة"}
              </h3>
              <div className="space-y-4">
                <Field label="عنوان الفرصة" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="opp-title" />
                <Field label="اسم البرنامج" value={form.program_name} onChange={(v) => setForm({ ...form, program_name: v })} testid="opp-program" />
                <Field label="الوصف" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea rows={4} testid="opp-desc" />

                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label={isAdmin ? "المدينة الافتراضية" : "المدينة"}
                    value={form.city}
                    onChange={(v) => setForm({ ...form, city: v })}
                    testid="opp-city"
                  />
                  <Field label="التصنيف" value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="opp-category" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="عدد المقاعد" type="number" value={form.seats} onChange={(v) => setForm({ ...form, seats: v })} testid="opp-seats" />
                  <div><label className="block text-sm font-medium text-slate-700 mb-1.5">المدة (بالأسابيع)</label><select value={form.duration_weeks || 12} onChange={(e) => setForm({ ...form, duration_weeks: Number(e.target.value) })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3" data-testid="opp-duration">{Array.from({ length: 48 }, (_, index) => index + 1).map((week) => <option key={week} value={week}>{week} أسبوع</option>)}</select></div>
                </div>
                <Field label="إجمالي عدد ساعات الفرصة التدريبية" type="number" value={form.total_training_hours} onChange={(v) => setForm({ ...form, total_training_hours: v })} testid="opp-total-hours" />
                <div><label className="block text-sm font-medium text-slate-700 mb-2">صورة غلاف الفرصة</label><div className="rounded-xl border border-dashed border-slate-300 p-3">{form.cover_image_url && <img src={form.cover_image_url} alt="" className="h-32 w-full object-cover rounded-lg mb-3" />}<label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-50 text-teal-800 font-bold cursor-pointer">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} رفع صورة<input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={uploading} onChange={(e) => uploadCover(e.target.files?.[0])} data-testid="opp-image-upload" /></label></div></div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="بداية التدريب" type="date" value={(form.training_start || "").slice(0,10)} onChange={(v) => setForm({ ...form, training_start: v })} />
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5">نهاية التدريب (تُحسب تلقائياً)</label>
                    <input
                      type="text"
                      readOnly
                      value={form.training_start ? (() => {
                        const w = Number(form.duration_weeks) || 12;
                        const s = new Date(form.training_start);
                        if (isNaN(s)) return "";
                        const e = new Date(s); e.setDate(e.getDate() + w * 7);
                        return e.toISOString().slice(0, 10);
                      })() : ""}
                      placeholder="اختر بداية التدريب والمدة"
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 arabic-numerals cursor-not-allowed"
                    />
                  </div>
                </div>
                <Field label="المتطلبات (كل سطر متطلّب)" value={form.requirements} onChange={(v) => setForm({ ...form, requirements: v })} textarea rows={3} testid="opp-req" />
                <Field label="المهام (ما هي أهم المهام المطلوبة من الطالب؟)" value={form.outcomes} onChange={(v) => setForm({ ...form, outcomes: v })} textarea rows={3} testid="opp-out" />

                {isAdmin && (
                  <OpportunityAllocationsEditor
                    value={form.association_allocations || []}
                    associations={associations}
                    onChange={(association_allocations) =>
                      setForm({ ...form, association_allocations })
                    }
                  />
                )}

                {isAdmin && (
                  <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      ملاحظات مدير النظام (خاصة — لا تظهر للجمعيات ولا الطلاب)
                    </label>
                    <textarea
                      rows={3}
                      value={form.admin_notes || ""}
                      onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
                      className="w-full rounded-lg border border-amber-300 bg-white px-4 py-3 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                      data-testid="opp-admin-notes"
                      placeholder="اكتب ملاحظاتك الإدارية على الفرصة"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">الحالة</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                    data-testid="opp-status-select"
                  >
                    {Object.entries(OPP_STATUS_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>

                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">المقرر التدريبي المرتبط (اختياري)</label>
                    <select
                      value={form.course_id || ""}
                      onChange={(e) => setForm({ ...form, course_id: e.target.value })}
                      className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                      data-testid="opp-course-select"
                    >
                      <option value="">بدون مقرر</option>
                      {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">عند قبول الطالب، سيتم تسجيله تلقائياً في المقرر المرتبط.</p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3">
                <button onClick={() => setShowForm(false)} className="flex-1 px-5 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100">
                  إلغاء
                </button>
                <button
                  onClick={handleSave} disabled={saving}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60"
                  data-testid="save-opp-btn"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  حفظ
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Field({ label, value, onChange, textarea, rows = 2, type = "text", testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      {textarea ? (
        <textarea
          rows={rows} value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
          data-testid={testid}
        />
      ) : (
        <input
          type={type} value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
          data-testid={testid}
        />
      )}
    </div>
  );
}

function OpportunityAllocationsEditor({
  value,
  associations,
  onChange,
}) {
  const DELIVERY_LABEL = { onsite: "حضوري", remote: "عن بُعد", hybrid: "هجين" };

  const addAllocation = () => {
    const firstAssociation = associations[0];
    onChange([
      ...value,
      {
        association_id: firstAssociation?.id || "",
        city: firstAssociation?.city || "",
        delivery_mode: "onsite",
        seats: 5,
      },
    ]);
  };

  const updateAllocation = (index, changes) => {
    onChange(
      value.map((allocation, currentIndex) =>
        currentIndex === index
          ? { ...allocation, ...changes }
          : allocation
      )
    );
  };

  const removeAllocation = (index) => {
    onChange(
      value.filter((_, currentIndex) => currentIndex !== index)
    );
  };

  const totalSeats = value.reduce(
    (total, allocation) =>
      total + (Number(allocation.seats) || 0),
    0
  );

  return (
    <section
      className="rounded-2xl border border-teal-100 bg-teal-50/40 p-4"
      data-testid="opportunity-allocations"
    >
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h4 className="font-bold text-slate-900">
            الجمعيات ومواقع التنفيذ
          </h4>
          <p className="mt-1 text-xs text-slate-600">
            أضف الجمعية والنمط والمدينة والمقاعد. يبقى التسكين النهائي بيد النظام.
          </p>
        </div>
        <button
          type="button"
          onClick={addAllocation}
          disabled={associations.length === 0}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-white px-4 text-sm font-bold text-teal-800 ring-1 ring-teal-200 disabled:opacity-50"
          data-testid="add-opportunity-allocation"
        >
          <Plus className="h-4 w-4" />
          إضافة جمعية
        </button>
      </div>

      {associations.length === 0 && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          لا توجد جمعيات نشطة متاحة للإسناد.
        </div>
      )}

      <div className="mt-4 space-y-3">
        {value.map((allocation, index) => {
          const selectedAssociation = associations.find(
            (association) =>
              association.id === allocation.association_id
          );
          const filled = Number(allocation.seats_filled) || 0;
          const mode = allocation.delivery_mode || "onsite";
          const isRemote = mode === "remote";

          return (
            <article
              key={allocation.id || `new-${index}`}
              className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid={`opportunity-allocation-${index}`}
            >
              <div className="mb-3 flex items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ${
                    mode === "remote"
                      ? "bg-sky-50 text-sky-700"
                      : mode === "hybrid"
                      ? "bg-violet-50 text-violet-700"
                      : "bg-teal-50 text-teal-700"
                  }`}
                  data-testid={`allocation-mode-badge-${index}`}
                >
                  {DELIVERY_LABEL[mode]}
                </span>
              </div>

              <div className="grid gap-3 md:grid-cols-[1.4fr_0.9fr_1fr_7rem_auto] md:items-end">
                <div>
                  <label className="mb-2 block text-xs font-semibold text-slate-700">
                    الجمعية
                  </label>
                  <select
                    value={allocation.association_id || ""}
                    onChange={(event) => {
                      const association = associations.find(
                        (item) => item.id === event.target.value
                      );
                      updateAllocation(index, {
                        association_id: event.target.value,
                        city: isRemote
                          ? ""
                          : allocation.city || association?.city || "",
                      });
                    }}
                    className="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
                    data-testid={`allocation-association-${index}`}
                  >
                    <option value="">اختر الجمعية</option>
                    {associations.map((association) => (
                      <option
                        key={association.id}
                        value={association.id}
                      >
                        {association.organization_name
                          || association.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-semibold text-slate-700">
                    نمط التنفيذ
                  </label>
                  <select
                    value={mode}
                    onChange={(event) => {
                      const nextMode = event.target.value;
                      updateAllocation(index, {
                        delivery_mode: nextMode,
                        city:
                          nextMode === "remote"
                            ? ""
                            : allocation.city
                              || selectedAssociation?.city
                              || "",
                      });
                    }}
                    className="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
                    data-testid={`allocation-mode-${index}`}
                  >
                    <option value="onsite">حضوري</option>
                    <option value="remote">عن بُعد</option>
                    <option value="hybrid">هجين</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-semibold text-slate-700">
                    مدينة التنفيذ
                  </label>
                  <input
                    value={isRemote ? "" : allocation.city || ""}
                    disabled={isRemote}
                    onChange={(event) =>
                      updateAllocation(index, {
                        city: event.target.value,
                      })
                    }
                    placeholder={
                      isRemote
                        ? "لا تنطبق"
                        : selectedAssociation?.city || "المدينة"
                    }
                    className="min-h-11 w-full rounded-lg border border-slate-300 px-3 text-sm disabled:bg-slate-100 disabled:text-slate-400"
                    data-testid={`allocation-city-${index}`}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-xs font-semibold text-slate-700">
                    المقاعد
                  </label>
                  <input
                    type="number"
                    min={Math.max(1, filled)}
                    max={10000}
                    value={allocation.seats || ""}
                    onChange={(event) =>
                      updateAllocation(index, {
                        seats: event.target.value,
                      })
                    }
                    className="min-h-11 w-full rounded-lg border border-slate-300 px-3 text-sm"
                    data-testid={`allocation-seats-${index}`}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => removeAllocation(index)}
                  disabled={filled > 0}
                  title={
                    filled > 0
                      ? "لا يمكن حذف موقع لديه طلاب مقبولون"
                      : "حذف موقع التنفيذ"
                  }
                  className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid={`remove-allocation-${index}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {filled > 0 && (
                <p className="mt-2 text-xs text-slate-500">
                  المقبولون حاليًا: {filled}
                </p>
              )}
            </article>
          );
        })}
      </div>

      {value.length === 0 && associations.length > 0 && (
        <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-white p-4 text-center text-sm text-slate-500">
          لم تضف أي موقع تنفيذ بعد.
        </div>
      )}

      {value.length > 0 && (
        <div className="mt-4 flex justify-between rounded-xl bg-teal-900 px-4 py-3 text-sm text-white">
          <span>إجمالي مواقع التنفيذ: {value.length}</span>
          <strong>إجمالي المقاعد: {totalSeats}</strong>
        </div>
      )}
    </section>
  );
}
